import type { ResponseTier } from '@/types/api.types';

/**
 * The fields `selectAutoAcceptable` needs from a question. `DdqSessionQuestion`
 * satisfies this structurally, so callers pass full questions and get the same
 * concrete type back.
 */
export interface AutoAcceptCandidate {
  question_id: string;
  response_tier: ResponseTier;
  answer_source?: 'vault_data' | 'knowledge_base' | 'llm' | 'firm_required' | null;
  userAction?: 'Pending' | 'Accepted' | 'Modified' | 'Rejected';
}

/**
 * Returns the questions that are safe to auto-accept in a single batch: an
 * `Exact` tier match that is grounded in live vault data and has not yet been
 * actioned. These are the answers the pipeline is most confident in, so the
 * reviewer can approve them as a group and spend attention on the tail.
 *
 * A missing `userAction` is treated as `Pending`.
 */
export function selectAutoAcceptable<T extends AutoAcceptCandidate>(questions: readonly T[]): T[] {
  return questions.filter(
    (q) =>
      q.response_tier === 'Exact' &&
      q.answer_source === 'vault_data' &&
      (q.userAction ?? 'Pending') === 'Pending',
  );
}
